import React from 'react';
import { User } from '@phosphor-icons/react';
import { useUserInfo } from '@/provider/UserInfoProvider';
import { getAuth } from 'firebase/auth';
import { app } from '@/config/firebaseConfig';

import { unsubscribeEndpoint } from '@/config/endpoints';
import { usePathname } from 'next/navigation';
import ConfirmationModal from '@/components/dashboard/confirmationUnsubscribe';
import useRouteLogin from '@/ueshook/useRouteLogin';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import useRoute from '@/ueshook/useRoute';
import { kazeFetch } from '@/utils/kazeFetch';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const UserInfoModal: React.FC<ModalProps> = ({ isOpen, onClose }) => {
  const { userInfo, isLoadingUser, updateUserInfoAssets } = useUserInfo();
  const email = getAuth(app).currentUser?.email || '';
  const pathname = usePathname();
  const { push } = useRoute();
  const { routerLogin } = useRouteLogin();
  const [isConfirmModalOpen, setConfirmModalOpen] = React.useState(false);
  const [isUnsubscribing, setIsUnsubscribing] = React.useState(false);

  // 当模态框打开时，更新用户信息以获取最新的订阅状态
  React.useEffect(() => {
    if (isOpen) {
      // console.log(
      //   'Modal opened, current subscription status:',
      //   userInfo?.subscription
      // );
      updateUserInfoAssets();
    }
  }, [isOpen, updateUserInfoAssets, userInfo?.subscription]);

  // // 监听用户信息变化，打印订阅状态
  // React.useEffect(() => {
  //   if (userInfo) {
  //     console.log('User info updated, subscription status:', {
  //       in_subscription: userInfo.subscription.in_subscription,
  //       subscription_name: userInfo.subscription.subscription_name,
  //       start_at: userInfo.subscription.start_at,
  //       end_at: userInfo.subscription.end_at,
  //     });
  //   }
  // }, [userInfo]);

  const handleUnsubscription = async () => {
    try {
      setIsUnsubscribing(true);
      const userId = getAuth().currentUser?.uid;
      if (!userId) {
        routerLogin(() => pathname && push(pathname));
        return;
      }

      const response = await kazeFetch(unsubscribeEndpoint, { method: 'POST' });
      // console.log('unsubscribe response', response);

      // 取消订阅成功，更新用户信息
      await updateUserInfoAssets();
      // console.log('Unsubscription successful and user info updated');
    } catch (error) {
      console.error('Error unsubscribing:', error);

      // 即使 API 返回错误，也要更新用户信息以获取真实状态
      // 因为取消订阅可能已经成功，只是服务器返回了错误
      try {
        await updateUserInfoAssets();
        // console.log(
        //   'User info updated after error to check real subscription status'
        // );
      } catch (updateError) {
        console.error(
          'Failed to update user info after unsubscribe error:',
          updateError
        );
      }
    } finally {
      setIsUnsubscribing(false);
    }
  };

  const handleUnsubscribeClick = () => {
    setConfirmModalOpen(true);
  };

  const handleConfirmUnsubscribe = () => {
    setConfirmModalOpen(false);
    handleUnsubscription();
  };

  const handleCancelUnsubscribe = () => {
    setConfirmModalOpen(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className='flex max-h-[calc(100vh-300px)] w-11/12 max-w-none flex-row gap-8 rounded-lg p-0 lg:max-h-[600px] lg:w-2/3'>
        <div className='hidden w-1/4 flex-col border-r border-gray-200 px-4 py-12 text-left lg:flex'>
          <button className='flex w-full flex-row items-center gap-2 rounded-md bg-gray-100 px-4 py-2 text-base font-medium text-gray-800 duration-200 hover:bg-gray-100'>
            <User weight='bold' size={18} />
            Account
          </button>
        </div>
        {isLoadingUser ? (
          <div className='flex w-full flex-col gap-8 overflow-y-auto px-8 py-12 text-black lg:w-3/4'>
            <p>Loading...</p>
          </div>
        ) : userInfo ? (
          <div className='flex w-full flex-col gap-8 overflow-y-auto px-8 py-12 text-black lg:w-3/4'>
            <div className='flex flex-col justify-center gap-1 text-center'>
              <DialogTitle className='text-3xl font-semibold'>
                Account
              </DialogTitle>
              <DialogDescription className='text-base text-gray-500'>
                Manage your account information
              </DialogDescription>
            </div>
            <div className='flex flex-col gap-1 text-left'>
              <h2 className='border-b border-gray-200 pb-px text-lg font-semibold'>
                Email
              </h2>
              <div className='flex flex-col justify-start gap-4 pt-2 lg:flex-row lg:items-center lg:justify-between lg:gap-0'>
                <p className='text-base text-gray-900'>{email}</p>
                {/* <button className='w-fit rounded-full bg-blue-100 px-4 py-1 text-sm text-blue-500 hover:bg-blue-200'>
                  Change Email
                </button> */}
              </div>
            </div>
            <div className='flex flex-col gap-1 text-left'>
              <h2 className='border-b border-gray-200 pb-px text-lg font-semibold'>
                Subscription Status
              </h2>
              <div className='flex flex-col justify-start gap-4 pt-2 lg:flex-row lg:items-center lg:justify-between lg:gap-0'>
                <span
                  className={`w-fit rounded-full px-4 py-1 text-sm ${
                    userInfo.subscription.subscription_name === 'free'
                      ? 'bg-green-100 text-green-500'
                      : 'bg-purple-100 text-purple-500'
                  }`}
                >
                  {userInfo.subscription.subscription_name}
                </span>
                {userInfo.subscription.in_subscription && (
                  <button
                    onClick={handleUnsubscribeClick}
                    disabled={isUnsubscribing}
                    className={`w-fit rounded-full px-4 py-1 text-sm ${
                      isUnsubscribing
                        ? 'cursor-not-allowed bg-gray-200 text-gray-400'
                        : 'bg-red-200 text-red-500 hover:bg-red-300'
                    }`}
                  >
                    {isUnsubscribing ? 'Processing...' : 'Unsubscribe'}
                  </button>
                )}
              </div>
            </div>
            <div className='flex flex-col gap-1 text-left'>
              <h2 className='border-b border-gray-200 pb-px text-lg font-semibold'>
                Credits
              </h2>
              <p className='pt-2 text-sm text-gray-900'>
                {`available/total: ${userInfo.plan.available_count} / ${userInfo.plan.total_limit}`}
              </p>
            </div>
          </div>
        ) : (
          <div className='flex w-3/4 flex-col gap-8 overflow-y-auto px-8 py-12 text-black'>
            <p className='text-black'>Please log in first.</p>
          </div>
        )}
        <ConfirmationModal
          isOpen={isConfirmModalOpen}
          onConfirm={handleConfirmUnsubscribe}
          onCancel={handleCancelUnsubscribe}
          isLoading={isUnsubscribing}
        />
      </DialogContent>
    </Dialog>
  );
};

export default UserInfoModal;
